import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import RedistribuicaoModal from './components/modals/RedistribuicaoModal';
import usePagination10 from './components/hooks/usePagination10';
import useReferenceData from './components/hooks/useReferenceData';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

const emptyForm = { ano: '', idPrograma: '', idEscola: '', idConta: '', custeio: '', capital: '' };

function Redistribuicoes() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const isGestor = JSON.parse(localStorage.getItem('vinculo') || '{}')?.tipo === 'GEST';
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...emptyForm, idEscola: cnpj || '', ano: ano || '' });
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);
  const { programas, contas } = useReferenceData(token, cnpj);
  const displayed = items.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  useEffect(() => {
    if (!cnpj || !ano) { showDefaultModal('error', 'Escola ou ano não selecionado.'); setIsLoading(false); return; }
    if (!contas || contas.length === 0) { setIsLoading(true); return; }
    
    setIsLoading(true);
    axios.get(`${API_BASE_URL.REDISTRIBUICAO}/all`, {
      params: { idEscola: cnpj, ano }, headers: { Authorization: `Bearer ${token}` }
    })
      .then(async (resp) => {
        let redistribuicoes = resp.data || [];
        
        // Usar contas já carregadas pelo hook
        if (!contas || contas.length === 0) { setIsLoading(false); return; }
        
        // Para cada conta, verificar se existe redistribuição
        const novasRedistribuicoes = [];
        
        for (const conta of contas) {
          const existente = redistribuicoes.find(r => r.idConta === conta.numConta);
          
          if (existente) {
            novasRedistribuicoes.push(existente);
          } else {
            // Criar nova redistribuição com valores padrão
            const novaRedistribuicao = {
              ano: parseInt(ano),
              idPrograma: conta.idPrograma,
              idEscola: cnpj,
              idConta: conta.numConta,
              custeio: 100,
              capital: 0
            };
            try {
              await axios.post(`${API_BASE_URL.REDISTRIBUICAO}/save`, novaRedistribuicao, { headers: { Authorization: `Bearer ${token}` } });
              novasRedistribuicoes.push(novaRedistribuicao);
              console.log('Redistribuição criada para:', conta.numConta);
            } catch (err) {
              console.log('Erro ao criar redistribuição para conta', conta.numConta, err.response?.data || err.message);
            }
          }
        }
        
        setItems(novasRedistribuicoes);
      })
      .catch(err => {
        if (err.response && err.response.status !== 200 && err.response.status !== 404) {
          showDefaultModal('error', 'Erro ao carregar redistribuições.');
        }
      })
      .finally(() => setIsLoading(false));
  }, [token, cnpj, ano, atualizar, contas]);

  function handleChange(e) {
    const { name, value } = e.target;
    if (name === 'custeio') {
      const v = value === '' ? '' : Math.min(100, Math.max(0, parseInt(value, 10) || 0));
      setForm(f => ({ ...f, custeio: v, capital: v === '' ? '' : 100 - v }));
    } else if (name === 'capital') {
      const v = value === '' ? '' : Math.min(100, Math.max(0, parseInt(value, 10) || 0));
      setForm(f => ({ ...f, capital: v, custeio: v === '' ? '' : 100 - v }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  }
  function handleNew() { setForm({ ...emptyForm, idEscola: cnpj || '', ano: ano || '', _isEditing: false }); setShowModal(true); }
  function handleEdit() { if (selected.length !== 1) return; setForm({ ...selected[0], _isEditing: true }); setShowModal(true); }

  function handleSave(e) {
    e.preventDefault();
    axios.post(`${API_BASE_URL.REDISTRIBUICAO}/save`, form, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]); // Limpa seleção após salvar
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar redistribuição.'));
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s => axios.post(`${API_BASE_URL.REDISTRIBUICAO}/delete`, {
      ano: s.ano, idPrograma: s.idPrograma, idEscola: s.idEscola, idConta: s.idConta
    }, { headers: { Authorization: `Bearer ${token}` } })))
      .then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir redistribuição.'))
    );
  }

  function keyOf(item) { return `${item.ano}_${item.idPrograma}_${item.idEscola}_${item.idConta}`; }
  function toggleSelect(item) {
    setSelected(prev => prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]);
  }

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="REDISTRIBUIÇÕES" icon="bi-arrow-left-right" />
        <PageContent>
          <Toolbar
            onEdit={!isGestor ? handleEdit : undefined} disableEdit={selected.length !== 1}
            onDelete={!isGestor ? handleDelete : undefined} disableDelete={selected.length === 0}
          />
          <TableContainer isLoading={false}>
                              <DataTable head={<tr>
                  <th style={{ width: '3rem' }}>#</th><th>Programa</th><th>Conta</th><th>Custeio</th><th>Capital</th>
                </tr>}>
                <tbody>
                  {displayed.map((item, idx) => (
                    <tr key={keyOf(item)} className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                      <td>{setPagination.startIndex + idx + 1}</td><td>{programas?.find(p => p.id === item.idPrograma)?.descricao || item.idPrograma}</td><td>{item.idConta}</td><td>{item.custeio}</td><td>{item.capital}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <RedistribuicaoModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} programas={programas} contas={contas} />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Redistribuicoes;
